import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Card, CardContent, IconButton, Typography, Button } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import styled from "@emotion/styled";
import { LineChart } from "@mui/x-charts/LineChart";
import type { IEnvironment, IEnvironmentMetricsDataPoint } from "../../../types/EnvironmentTypes";

const DraggableCard = styled(Card)<{ zIndex: number }>`
  position: absolute;
  z-index: ${(props) => props.zIndex};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  width: 700px;
`;

const DragHandle = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  cursor: move;
  color: rgba(255, 255, 255, 0.7);
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: rgba(255, 255, 255, 1);
  }
`;

const HeaderContainer = styled.div`
  height: 60px;
  background-color: #1976d2;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export interface EnvironmentMetricsPopupProps {
  environment: IEnvironment;
  zIndex: number;
  onClose: () => void;
  onFocus: () => void;
  onCleanupDisk: () => void;
  fetchMetrics: (name: string) => Promise<IEnvironmentMetricsDataPoint>;
}

interface MetricsDataPoint {
  timestamp: number;
  cpuUsage: number;
  memoryUsage: number;
  diskUsage: number;
}

const METRICS_DATA_POINTS_COUNT = 25;
const ENV_METRICS_POSITION_STORAGE_KEY = "environment-metrics-positions";

interface EnvironmentMetricsPosition {
  x: number;
  y: number;
}

const getStoredPositions = (): Map<string, EnvironmentMetricsPosition> => {
  const stored = localStorage.getItem(ENV_METRICS_POSITION_STORAGE_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      return new Map(Object.entries(parsed));
    } catch (_) {
      return new Map();
    }
  }
  return new Map();
};

const savePosition = (envName: string, position: EnvironmentMetricsPosition) => {
  const positions = getStoredPositions();
  positions.set(envName, position);
  const obj = Object.fromEntries(positions);
  localStorage.setItem(ENV_METRICS_POSITION_STORAGE_KEY, JSON.stringify(obj));
};

export const EnvironmentMetricsPopup = ({
  environment,
  zIndex,
  onClose,
  onFocus,
  onCleanupDisk,
  fetchMetrics,
}: EnvironmentMetricsPopupProps) => {
  const [metricsData, setMetricsData] = useState<MetricsDataPoint[]>(
    Array.from({ length: METRICS_DATA_POINTS_COUNT }, (_, index) => ({
      timestamp: Date.now() - (METRICS_DATA_POINTS_COUNT - index - 1) * 3000,
      cpuUsage: 0,
      memoryUsage: 0,
      diskUsage: 0,
    })),
  );
  const [position, setPosition] = useState<EnvironmentMetricsPosition | undefined>(undefined);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const cardRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const positions = getStoredPositions();
    const savedPosition = positions.get(environment.name);

    if (savedPosition) {
      setPosition(savedPosition);
    } else {
      // Calculate offset position for new popups
      if (cardRef.current && typeof window !== "undefined") {
        const { width, height } = cardRef.current.getBoundingClientRect();
        const centerX = (window.innerWidth - width) / 2;
        const centerY = (window.innerHeight - height) / 3;
        // Add a small random offset so multiple popups don't stack exactly on top of each other
        const offsetX = Math.random() * 100 - 50;
        const offsetY = Math.random() * 100 - 50;
        setPosition({ x: centerX + offsetX, y: centerY + offsetY });
      }
    }
  }, [environment.name]);

  useEffect(() => {
    if (position) {
      savePosition(environment.name, position);
    }
  }, [position, environment.name]);

  const handleMouseDown = (e: React.MouseEvent) => {
    onFocus();
    if (cardRef.current) {
      const { left, top } = cardRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - left,
        y: e.clientY - top,
      });
      setIsDragging(true);
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const x = e.clientX - dragOffset.x;
      const y = e.clientY - dragOffset.y;
      setPosition({ x, y });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  const fetchAndUpdateMetrics = async () => {
    try {
      const metrics = await fetchMetrics(environment.name);
      const timestamp = Date.now();
      const cpuUsage = metrics.cpuUsagePercent;
      const memoryUsage = (metrics.memoryUsedMb / metrics.memoryTotalMb) * 100;
      const diskUsage = (metrics.diskUsedGb / metrics.diskTotalGb) * 100;

      setMetricsData((prevData) => {
        const newData = [
          ...prevData.slice(1), // Remove the first element
          {
            timestamp,
            cpuUsage,
            memoryUsage,
            diskUsage,
          },
        ];
        // Keep exactly METRICS_DATA_POINTS_COUNT data points
        return newData;
      });
    } catch (error) {
      console.error("Failed to fetch metrics:", error);
    }
  };

  useEffect(() => {
    // Fetch immediately on mount
    fetchAndUpdateMetrics();

    // Set up interval to fetch every 3 seconds
    intervalRef.current = setInterval(fetchAndUpdateMetrics, 3000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment.name]);

  const handleCardClick = () => {
    onFocus();
  };

  return (
    <DraggableCard
      ref={cardRef}
      zIndex={zIndex}
      onClick={handleCardClick}
      style={{
        left: `${position?.x}px`,
        top: `${position?.y}px`,
        cursor: isDragging ? "grabbing" : "auto",
      }}
    >
      <DragHandle onMouseDown={handleMouseDown}>
        <DragIndicatorIcon />
      </DragHandle>
      <HeaderContainer>
        <Typography variant="h5" component="div" color="white">
          <b>Metrics: {environment.name}</b>
        </Typography>
      </HeaderContainer>
      <IconButton
        aria-label="close"
        color="info"
        size="large"
        style={{ position: "absolute", top: 1, right: 1, zIndex: 1002 }}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <CancelIcon />
      </IconButton>
      <CardContent>
        <div style={{ width: "100%", height: 300, marginTop: 16 }}>
          {metricsData.length > 0 ? (
            <LineChart
              xAxis={[
                {
                  data: metricsData.map((_, index) => index),
                  label: "Time (samples)",
                },
              ]}
              yAxis={[
                {
                  min: 0,
                  max: 100,
                  label: "Usage (%)",
                },
              ]}
              series={[
                {
                  data: metricsData.map((d) => d.cpuUsage),
                  label: "CPU Usage",
                  color: "#ff6b6b",
                  curve: "linear",
                },
                {
                  data: metricsData.map((d) => d.memoryUsage),
                  label: "Memory Usage",
                  color: "#4ecdc4",
                  curve: "linear",
                },
                {
                  data: metricsData.map((d) => d.diskUsage),
                  label: "Disk Usage",
                  color: "#ffe66d",
                  curve: "linear",
                },
              ]}
              height={300}
              slotProps={{
                // Fix to make sure the tooltip appears above the modal
                tooltip: {
                  sx: {
                    zIndex: zIndex + 1000,
                  },
                },
              }}
              skipAnimation
            />
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
              <Typography variant="body1" color="text.secondary">
                Loading metrics...
              </Typography>
            </div>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <Button
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              onCleanupDisk();
            }}
          >
            Clean-up Disk
          </Button>
        </div>
      </CardContent>
    </DraggableCard>
  );
};
